import React from 'react'
import * as Yup from 'yup'
import { Formik, Field } from 'formik'
import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'

import {
  Checkbox,
  CheckboxGroup,
  MultiSelect,
  RadioGroup,
  NestedSelectReadOnly,
  NestedSelect,
  Select,
  TextArea,
  TextField,
} from '.'

import { showDependentField } from './helpers'

import './AbstractForm.scss'

// Get default values, and if it's an editing form use any existing values
const getInitialValues = (initialValues, existingValues) => {
  if (!existingValues) return initialValues

  return { ...initialValues, ...existingValues }
}

const formComponents = [
  { type: 'text', component: TextField },
  { type: 'password', component: TextField },
  { type: 'email', component: TextField },
  { type: 'number', component: TextField },
  { type: 'textArea', component: TextArea },
  { type: 'select', component: Select },
  { type: 'multiSelect', component: MultiSelect },
  { type: 'radioGroup', component: RadioGroup },
  { type: 'checkbox', component: Checkbox },
  { type: 'checkboxGroup', component: CheckboxGroup },
  { type: 'nestedSelectReadOnly', component: NestedSelectReadOnly },
  { type: 'nestedSelect', component: NestedSelect },
]

/**
 * @import import { AbstractForm } from '@/components/forms/formik/AbstractForm'
 * @param {object} props
 * @param {object[]} props.schema Field definitions rendered in order; each item takes type ('text', 'password', 'email', 'number', 'textArea', 'select', 'multiSelect', 'radioGroup', 'checkbox', 'checkboxGroup', 'nestedSelect', 'nestedSelectReadOnly'), name, label, plus per-type extras (options as {value, label}[], required, defaultValue, disabled, dependencies as {key, operator, value}[] to show the field conditionally, isStatic to render a heading instead of a field, raw + as to inject custom JSX, labelTooltip to hang the field's help text off an InfoTooltip on the label instead of printing it underneath)
 * @param {Function} props.onSubmit Receives the Formik values when the form submits
 * @param {object} [props.existingValues] Pre-populates fields for edit forms, merged over schema defaults
 * @param {Function} [props.onClose] Renders a cancel button that calls this
 * @param {object} [props.buttonProps] Extra props spread onto the submit Button
 * @param {object} [props.closeButtonProps] Extra props spread onto the cancel Button
 * @param {string} [props.buttonLabel] Submit button label, defaults to Submit
 * @param {string} [props.closeButtonLabel] Cancel button label, defaults to Cancel
 * @param {string} [props.buttonAlignment] 'left' aligns the action buttons left instead of right
 * @param {boolean} [props.horizontal] Lays fields out in a row instead of stacked
 * @param {Function} [props.onDirty] Called with the form's dirty state on every render
 * @param {Function} [props.onFormikReady] Receives {setFieldValue, setValues, values, initialValues, dirty, resetForm} for external field control
 * @param {boolean} [props.isModal] Styles the action bar for modal footers
 * @param {React.ReactNode} [props.footerStart] Leading footer actions, rendered left of Close/Submit
 * @param {boolean} [props.noLabel] Hides field labels
 */
export const AbstractForm = ({
  schema,
  onSubmit,
  existingValues,
  onClose,
  buttonProps = {},
  closeButtonProps = {},
  buttonLabel = 'Submit',
  closeButtonLabel = 'Cancel',
  buttonAlignment = 'right',
  footerStart,
  horizontal,
  onDirty = () => {},
  onFormikReady = () => {},
  isModal,
  noLabel,
  ...props
}) => {
  // Set initial values
  const initialValues = schema.reduce((acc, val) => {
    let defaultValue
    switch (val.type) {
      case 'text':
      case 'password':
      case 'email':
      case 'textArea':
      case 'select':
      case 'radioGroup':
        defaultValue = ''
        break
      case 'checkboxGroup':
      case 'multiSelect':
        defaultValue = []
        break
      case 'checkbox':
        defaultValue = false
        break
      case 'nestedSelectReadOnly':
        defaultValue = undefined
        break
      case 'nestedSelect':
        defaultValue = []
        acc[`${val.name}Includes`] = true
        break
      default:
        defaultValue = ''
    }
    return { ...acc, [val.name]: val.defaultValue || defaultValue, validateOnMount: true }
  }, {})

  // Set validation object
  const cyclicDependencyArray = []
  const validationObject = schema.reduce((acc, val) => {
    let validationType

    switch (val.type) {
      case 'text':
      case 'password':
      case 'textArea':
      case 'select':
      case 'radio':
      case 'radioGroup':
        validationType = Yup.string()
        break
      case 'email':
        validationType = Yup.string().email('Must be a valid email')
        break
      case 'number':
        validationType = Yup.number().typeError('Must be a valid number')
        break
      case 'checkboxGroup':
      case 'multiSelect':
        validationType = Yup.array()

        if (val.required) {
          validationType = validationType.min(1, `${val.label} requires at least one option`)
        }
        break
      case 'checkbox':
        if (val.required) {
          validationType = Yup.boolean().oneOf([true], `${val.label} must be selected`)
        }
        break
      case 'nestedSelectReadOnly':
        validationType = null
        break
      case 'nestedSelect':
        validationType = null
        break
      default:
        validationType = null
    }

    if (val.required && validationType) {
      validationType = validationType.required(`${val.label} is required`)
    }

    if (val.nullable && validationType) {
      validationType = validationType.nullable(true)
    }

    if (val.customValidators) {
      val.customValidators.forEach((validation) => {
        validationType = validation
      })
      // Handle cyclic dependencies by collecting [fieldName, dependency] pairs
      // which are passed as the second argument to Yup.object().shape()
      // so Yup can resolve mutually dependent fields during validation.
      if (val.validationDependsOn) {
        cyclicDependencyArray.push([val.name, val.validationDependsOn])
      }
    }

    return { ...acc, ...(validationType && { [val.name]: validationType }) }
  }, {})
  const validationSchema = Yup.object().shape(validationObject, cyclicDependencyArray)
  const customOnChange = (name, value, fn, onChange) => {
    if (value) {
      fn(value)
    }
    onChange(name, value)
  }
  return (
    <Formik
      initialValues={getInitialValues(initialValues, existingValues)}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
      {...props}
    >
      {({
        handleSubmit,
        isSubmitting,
        isValid,
        values,
        initialValues: formInitialValues,
        setFieldValue,
        setValues,
        dirty,
        resetForm,
      }) => {
        onDirty(dirty)
        onFormikReady({
          setFieldValue,
          setValues,
          values,
          initialValues: formInitialValues,
          dirty,
          resetForm,
        })

        return (
          <form
            onSubmit={handleSubmit}
            data-testid="abstract-form"
            className={classNames('abstract-form', { horizontal, 'no-label': noLabel })}
          >
            {schema.map(
              ({ type, inputType, customValidators, validationDependsOn, ...formSchema }) => {
                if (formSchema.dependencies && !showDependentField(formSchema, values)) {
                  // if the field disappears, reset value we don't want a lingering value to be submitted
                  if (values[formSchema.name] !== initialValues[formSchema.name]) {
                    setFieldValue(formSchema.name, initialValues[formSchema.name])
                  }
                  return null
                }

                // for injecting customized fields or other html for display purposes
                if (formSchema.raw) {
                  return formSchema.as
                }
                // if the field is disabled set the initial values
                if (formSchema.disabled && formSchema.resetOnDisable) {
                  if (values[formSchema.name] !== initialValues[formSchema.name]) {
                    setFieldValue(formSchema.name, initialValues[formSchema.name])
                  }
                }

                if (formSchema.isStatic) {
                  const HeadingElement = formSchema.as || 'h4'
                  return <HeadingElement key={formSchema.name}>{formSchema.label}</HeadingElement>
                }

                if (!formComponents.some((formComponent) => formComponent.type === type))
                  return null

                const Component = formComponents.find(
                  (formComponent) => formComponent.type === type
                ).component

                return (
                  <Field
                    key={formSchema.name}
                    name={formSchema.name}
                    type={inputType}
                    validate={formSchema?.validate}
                  >
                    {(fieldProps) => {
                      // allows for custom logic when field value is changed
                      const customComponentProps = {}
                      if (formSchema.onChange) {
                        customComponentProps.onChange = (value) =>
                          customOnChange(
                            formSchema.name,
                            value,
                            formSchema.onChange,
                            fieldProps.field.onChange
                          )
                      }

                      return (
                        <Component
                          {...fieldProps}
                          {...formSchema}
                          {...customComponentProps}
                          type={inputType}
                        />
                      )
                    }}
                  </Field>
                )
              }
            )}
            <div
              className={classNames('form-actions', {
                'left-aligned': buttonAlignment === 'left',
                'is-modal': isModal,
              })}
            >
              {footerStart && <div className="form-actions__start">{footerStart}</div>}
              {!!onClose && (
                <Button
                  data-testid="abstract-form-close"
                  type="button"
                  onClick={onClose}
                  {...closeButtonProps}
                >
                  {closeButtonLabel}
                </Button>
              )}
              <Button
                data-testid="abstract-form-submit"
                brand
                type="submit"
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
                {...buttonProps}
              >
                {buttonLabel}
              </Button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
